import { toLocale } from '@zuruflex/i18n';
import type { Promotion } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  StatTile,
  Tabs,
  TabsList,
  TabsTrigger,
  type ColumnDef,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { CirclePause, Plus, Tag, TicketPercent } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PromotionDialog } from '../components/PromotionDialog.js';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { PHASE_VARIANT, promoPhase, promoValue, promoWindow } from '../lib/promotions.js';
import { useTableLabels } from '../lib/table-labels.js';

const FILTERS = ['all', 'ACTIVE', 'PAUSED', 'ARCHIVED'] as const;
type Filter = (typeof FILTERS)[number];

function isFilter(value: string | null): value is Filter {
  return value !== null && (FILTERS as readonly string[]).includes(value);
}

/**
 * The partner's own promos: codes they hand out and discounts that apply
 * themselves, on their own cars, at their own cost.
 *
 * The admin's page with the Zuruflex half taken away. Every row here is paid
 * for by this partner, so there is no funder column to read — the page
 * says it once, under its title, instead of eleven times down a table.
 *
 * Starting or changing one takes a manager: the API refuses a STAFF user,
 * so the button is not offered to one rather than offered and refused.
 */
export function PromotionsPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { user } = usePartner();
  const canManage = user.role === 'OWNER' || user.role === 'MANAGER';
  const navigate = useNavigate();
  const tableLabels = useTableLabels();
  const [creating, setCreating] = useState(false);

  const [params, setParams] = useSearchParams();
  const fromParam = params.get('show');
  const filter: Filter = isFilter(fromParam) ? fromParam : 'all';

  /*
   * One page of a hundred, filtered here rather than per tab. A partner
   * runs a handful of promos, and the tab counts need all of them anyway.
   */
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['my-promotions'],
    queryFn: () => api.listPromotions({ perPage: 100 }),
  });

  const all = useMemo(() => data?.items ?? [], [data]);
  const shown =
    filter === 'all'
      ? all.filter((row) => row.status !== 'ARCHIVED')
      : all.filter((row) => row.status === filter);

  const now = Date.now();
  const live = all.filter((row) => promoPhase(row, now) === 'ACTIVE').length;
  const paused = all.filter((row) => row.status === 'PAUSED').length;
  const used = all.reduce((sum, row) => sum + row.redemptionCount, 0);

  const columns = useMemo<ColumnDef<Promotion>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('promotions.col.name'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-text">{row.original.name}</p>
            <p className="text-xs text-text-muted">
              {row.original.vehicleIds.length === 0
                ? t('promotions.allCars')
                : t('promotions.cars', { count: row.original.vehicleIds.length })}
              {row.original.audience === 'FIRST_HIRE' ? ` · ${t('promotions.firstHire')}` : ''}
            </p>
          </div>
        ),
      },
      {
        id: 'code',
        header: t('promotions.col.code'),
        cell: ({ row }) =>
          row.original.code ? (
            <span className="font-mono text-sm text-text">{row.original.code}</span>
          ) : (
            <span className="text-sm text-text-muted">{t('promotions.automatic')}</span>
          ),
      },
      {
        id: 'value',
        header: t('promotions.col.value'),
        cell: ({ row }) => (
          <span className="tabular-nums">{promoValue(row.original, t, locale)}</span>
        ),
      },
      {
        id: 'uses',
        header: t('promotions.col.uses'),
        cell: ({ row }) => (
          <span className="tabular-nums text-text-muted">
            {row.original.maxRedemptions
              ? t('promotions.usesOf', {
                  count: row.original.redemptionCount,
                  max: row.original.maxRedemptions,
                })
              : row.original.redemptionCount}
          </span>
        ),
      },
      {
        id: 'runs',
        header: t('promotions.col.runs'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">{promoWindow(row.original, t, locale)}</span>
        ),
      },
      {
        id: 'status',
        header: t('promotions.col.status'),
        cell: ({ row }) => {
          const phase = promoPhase(row.original);
          return <Badge variant={PHASE_VARIANT[phase]}>{t(`promotions.status.${phase}`)}</Badge>;
        },
      },
    ],
    [t, locale],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const selectFilter = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'all') nextParams.delete('show');
    else nextParams.set('show', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={t('promotions.title')}
        description={t('promotions.subtitle')}
        action={
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t('promotions.new')}
            </Button>
          ) : null
        }
      />

      {canManage ? null : (
        <Alert variant="info" className="mb-6">
          {t('promotions.managerOnly')}
        </Alert>
      )}

      {!isPending && all.length === 0 ? (
        <EmptyState
          title={t('promotions.none')}
          description={t('promotions.noneBody')}
          action={
            canManage ? (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" />
                {t('promotions.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile
              label={t('promotions.summary.live')}
              value={String(live)}
              hint={t('promotions.summary.liveHint')}
              icon={<TicketPercent className="size-4" />}
              isLoading={isPending}
              accent
            />
            <StatTile
              label={t('promotions.summary.used')}
              value={String(used)}
              hint={t('promotions.summary.usedHint')}
              icon={<Tag className="size-4" />}
              isLoading={isPending}
            />
            <StatTile
              label={t('promotions.summary.paused')}
              value={String(paused)}
              hint={t('promotions.summary.pausedHint')}
              icon={<CirclePause className="size-4" />}
              isLoading={isPending}
            />
          </div>

          <Tabs value={filter} onValueChange={selectFilter}>
            <TabsList className="flex-wrap">
              {FILTERS.map((key) => (
                <TabsTrigger key={key} value={key} className="gap-2">
                  {t(`promotions.filter.${key}`)}
                  <span className="text-xs tabular-nums opacity-60">
                    {key === 'all'
                      ? all.filter((row) => row.status !== 'ARCHIVED').length
                      : all.filter((row) => row.status === key).length}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <DataTable
            labels={tableLabels}
            columns={columns}
            data={shown}
            isLoading={isPending}
            onRowClick={(row) => navigate(`/promotions/${row.id}`)}
            emptyMessage={t('promotions.none')}
          />
        </div>
      )}

      {creating ? (
        <PromotionDialog
          open
          onOpenChange={(next) => {
            if (!next) setCreating(false);
          }}
          onSaved={(saved) => navigate(`/promotions/${saved.id}`)}
        />
      ) : null}
    </>
  );
}
